import { PageSubtitle } from "@components/common/page-subtitle";
import { useErrorHandlerContext } from "@context-providers/error-handler/error-handler-context";
import { useToast } from "@flat-ui";
import { SignupEmailIndicator } from "@components/signup/signup-email-indicator";
import { runtimeConfig } from "@src/runtime-config";
import { useEffect, useState } from "react";
import { InvitedSubtitle } from "@pages/invited/invited-subtitle";
import { RegisterableServer } from "@custom-types/server-types";
import { AuthMethod } from "@custom-types/signup-types";
import { SignupAuthMethod } from "@components/signup/signup-auth-method";
import { FaroCoreApiTypes } from "@stellar/core-api-client";
import { ulmConfigClientSelector } from "@store/ulm-config/ulm-config-selector";
import { useAppSelector } from "@store/store-helper";
import { redirectClient } from "@utils/client-utils";
import { InvitedProps } from "@pages/invited/invited-types";
import { useGetRegisterableServer } from "@hooks/use-get-registerable-server";
import { isPreassignedServerWithoutGenericOAuth } from "@utils/servers-utils";

interface Props extends InvitedProps {
  /** Server already validated for the email. If passed, the email is not checked again */
  server?: RegisterableServer;
}

/**
 * Renders the first step of the invited user registration flow.
 * It first checks the status of the passed email address and then
 * it displays the available authentication methods for the user to select.
 * If the user selects an SSO authentication method then only a redirection
 * to the Core API OAuth route is performed.
 * If the user selects email/password authentication method then the flow
 * continues to the next step to set up the account details.
 */
export function InvitedStepOne({
  setStep,
  email,
  server: validatedServer,
}: Props): JSX.Element {
  const { handleError } = useErrorHandlerContext();
  const { closeToast, openToast } = useToast();
  const client = useAppSelector(ulmConfigClientSelector);
  const { getRegisterableServer, isFetchingServer } =
    useGetRegisterableServer();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [server, setServer] = useState<RegisterableServer | undefined>(
    validatedServer
  );

  /** Handles the selection of email/password authentication method */
  function handleSignUpWithEmail(): void {
    setStep("two");
  }

  /** Handles the selection of an SSO authentication method */
  function handleSignUpWithSSO(provider: FaroCoreApiTypes.OAuthProvider): void {
    const toastKey = openToast({
      title: "You are being redirected...",
      variant: "neutral",
    });

    try {
      // Use the loginUrl from the server's loginProvider if available,
      // as it already contains the email query parameter for SSO signups.
      const baseUrl =
        server && isPreassignedServerWithoutGenericOAuth(server)
          ? server.loginProvider.loginUrl
          : `${runtimeConfig.urls.apiBaseUrl}/oauth?provider=${provider}`;

      const loginUrl = new URL(baseUrl);
      loginUrl.searchParams.set(
        "redirectUrl",
        runtimeConfig.urls.sphereDashboardUrl
      );

      // Keep backend-provided email hints, but ensure user_not_found OAuth URLs
      // also carry the invited email so identity provider pages can prefill it.
      if (!loginUrl.searchParams.has("email")) {
        loginUrl.searchParams.set("email", email);
      }

      redirectClient({
        client,
        redirectUrl: loginUrl.href,
      });
    } catch (error) {
      closeToast(toastKey);
      handleError({
        id: `handleSignUpWithSSO-${Date.now().toString()}`,
        title: "An error occurred during redirection.",
        error,
        uiType: "toast",
      });
    }
  }

  /** Handles the selection of an authentication method */
  function handleAuthMethodSelection(authMethod: AuthMethod): void {
    authMethod === "email"
      ? handleSignUpWithEmail()
      : handleSignUpWithSSO(authMethod);
  }

  // Fetch the registerable server associated with the provided email address
  useEffect(() => {
    if (validatedServer) {
      return;
    }

    async function getServer(): Promise<void> {
      const server = await getRegisterableServer(email);
      if (server) {
        setServer(server);
      }
    }

    void getServer();
  }, [email, getRegisterableServer, validatedServer]);

  return (
    <>
      {isFetchingServer && (
        <PageSubtitle>Validating your email...</PageSubtitle>
      )}
      {!isFetchingServer && <InvitedSubtitle />}
      <SignupEmailIndicator
        email={email}
        sx={{
          marginTop: "16px",
        }}
      />
      {server && (
        <SignupAuthMethod
          email={email}
          server={server}
          setAuthMethod={handleAuthMethodSelection}
          isLoading={isLoading}
          setIsLoading={setIsLoading}
        />
      )}
    </>
  );
}
